// Single source of truth for the capability pages under /funcionalidades. The
// header's desktop dropdown, its mobile accordion, the /funcionalidades hub
// and (from plan 15-06) the home page's teaser section all derive their
// entries from this ONE array — no consumer hardcodes its own slug list, so a
// partial or drifting listing across surfaces is structurally impossible.
//
// Array order IS presentation order everywhere this constant is consumed.
//
// This plan (15-01, the phase's tracer slice) seeds the array with exactly
// ONE entry, transcripcion-de-sesiones. Plans 15-02 through 15-04 each append
// their entry in the SAME commit as the page it points at, so the constant
// never references a route that does not exist yet (FEAT-04).

export interface EntradaFuncionalidad {
    /** Lowercase ASCII, accents folded, words hyphenated — matches /^[a-z0-9-]+$/. */
    slug: string;
    /** Short Label-role name: the capability, not the outcome (D-01). Used by the dropdown and the breadcrumb. */
    etiqueta: string;
    /** One-line outcome sentence the hub renders under `etiqueta`. */
    descripcion: string;
    /**
     * Optional explainer-video metadata (VID-05, D-07). An OBJECT, never an
     * array — exactly one video per capability page by construction. Populated
     * incrementally as Phase 16 ships each capability's video; an entry
     * without it renders no video section at all (see VideoExplicativo and
     * construirGrafoVideo, both of which treat `undefined` as "nothing to
     * show" rather than a placeholder). `ruta`/`poster` must resolve to real
     * files under `public/video/`; the `.vtt` captions path is NOT a sixth
     * field — it is derived from `slug` by convention, matching the render
     * and poster naming (`public/video/<slug>.vtt`).
     */
    video?: {
        /** Path under `public/`, e.g. "/video/<slug>.mp4". */
        ruta: string;
        /** Path under `public/`, e.g. "/video/<slug>-poster.jpg". */
        poster: string;
        /** Measured render duration, never an estimate. */
        duracionSegundos: number;
        ancho: number;
        alto: number;
    };
}

// Orden aprobado por el operador el 2026-09-03 (ver
// .planning/todos/pending/recorrido-paso-a-paso-entre-funcionalidades.md):
// el orden del array pasa a ser el orden del flujo de trabajo real dentro de
// la app, no el orden comercial anterior. Como el comentario de arriba deja
// dicho, ese orden ES el orden de presentación en todas partes — el
// desplegable, el hub y el teaser del home lo heredan sin tocar código propio.
export const FUNCIONALIDADES_NAV: EntradaFuncionalidad[] = [
    {
        slug: 'documentos-del-paciente',
        etiqueta: 'Documentos del paciente',
        descripcion: 'Sube historiales antiguos en PDF y ten una visión completa desde el primer día.',
        video: {
            ruta: '/video/documentos-del-paciente.mp4',
            poster: '/video/documentos-del-paciente-poster.jpg',
            duracionSegundos: 31.57,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'tests-psicometricos',
        etiqueta: 'Tests psicométricos',
        descripcion: 'Los resultados de las pruebas, adjuntos a la sesión en la que se pasaron.',
        video: {
            ruta: '/video/tests-psicometricos.mp4',
            poster: '/video/tests-psicometricos-poster.jpg',
            duracionSegundos: 37.6,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'transcripcion-de-sesiones',
        etiqueta: 'Transcripción de sesiones',
        descripcion: 'De la grabación a un texto listo para revisar, sin escribir una palabra.',
        video: {
            ruta: '/video/transcripcion-de-sesiones.mp4',
            poster: '/video/transcripcion-de-sesiones-poster.jpg',
            // Medido con ffprobe sobre el render final (renders/transcripcion-de-sesiones.mp4
            // -> psicoguia/public/video/transcripcion-de-sesiones.mp4), nunca una estimación.
            duracionSegundos: 35.47,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'analisis-clinico-dsm-5',
        etiqueta: 'Análisis clínico y DSM-5',
        // La referencia DSM-5 la responde un modelo desde su propio conocimiento: NO es
        // una búsqueda sobre un manual almacenado ni indexado. La redacción anterior
        // ("consulta el manual DSM-5") prometía un cotejo documental que el producto no
        // hace — ver CLM-06 en la fase 17.
        descripcion: 'Ten los criterios diagnósticos a mano mientras registras tu propia hipótesis.',
        video: {
            ruta: '/video/analisis-clinico-dsm-5.mp4',
            poster: '/video/analisis-clinico-dsm-5-poster.jpg',
            // D-10 (fase 17, plan 06): re-medido tras añadir la escena 5
            // (extracción descriptiva a petición) -- antes 32.2s de 5 escenas.
            duracionSegundos: 40.93,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'evidencia-cientifica',
        etiqueta: 'Evidencia científica',
        descripcion: 'Respaldo teórico para tus hipótesis diagnósticas, apoyado en el DSM-5.',
        video: {
            ruta: '/video/evidencia-cientifica.mp4',
            poster: '/video/evidencia-cientifica-poster.jpg',
            duracionSegundos: 30.73,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'notas-de-progreso',
        etiqueta: 'Notas de progreso',
        // Cinco formatos, no cuatro: el enum de progressNote.model.js incluye GIRP, que
        // se enviaba en producción sin anunciarse — ver CLM-03 en la fase 17.
        descripcion: 'Un borrador SOAP, DAP, BIRP, GIRP o PIRP listo para revisar después de cada sesión.',
        video: {
            ruta: '/video/notas-de-progreso.mp4',
            poster: '/video/notas-de-progreso-poster.jpg',
            duracionSegundos: 36.1,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'plan-de-tratamiento',
        etiqueta: 'Plan de tratamiento',
        descripcion: 'Objetivos, intervenciones y seguimiento del proceso en un solo lugar.',
        video: {
            ruta: '/video/plan-de-tratamiento.mp4',
            poster: '/video/plan-de-tratamiento-poster.jpg',
            duracionSegundos: 34.43,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'chatbot-con-historial-clinico',
        etiqueta: 'Chatbot con historial clínico',
        descripcion: 'Pregúntale al historial completo del paciente, no solo a la sesión de hoy.',
        video: {
            ruta: '/video/chatbot-con-historial-clinico.mp4',
            poster: '/video/chatbot-con-historial-clinico-poster.jpg',
            duracionSegundos: 29.13,
            ancho: 1920,
            alto: 1080,
        },
    },
    {
        slug: 'organizaciones-y-equipos',
        etiqueta: 'Organizaciones y equipos',
        // CLM-01 en fase 17: la organización comparte licencias y facturación, no el
        // historial clínico. Cada terapeuta conserva sus propios pacientes en privado.
        descripcion: 'Invita a tu equipo y gestiona licencias y facturación, sin compartir el historial clínico de nadie.',
        video: {
            ruta: '/video/organizaciones-y-equipos.mp4',
            poster: '/video/organizaciones-y-equipos-poster.jpg',
            duracionSegundos: 24.8,
            ancho: 1920,
            alto: 1080,
        },
    },
];
